<template>
    <div class="card border-0 bg-transparent">
        <div class="card-body p-0">
            <div class="datatable">
                <app-overlay-loader v-if="loading"/>
                <div v-if="rows.length > 0"
                     class="table-responsive custom-scrollbar table-view-responsive pt-3 mt-3"
                     :class="{'loading-opacity': loading}">
                    <table class="table mb-0">
                        <thead>
                        <tr>
                            <th class="datatable-th pt-2"><span class="font-size-default"><span>{{ $t('date') }}</span></span></th>
                            <th class="datatable-th pt-2"><span class="font-size-default"><span>{{ $t('hotel') }}</span></span></th>
                            <th class="datatable-th pt-2"><span class="font-size-default"><span>{{ $t('staff') }}</span></span></th>
                            <th class="datatable-th pt-2"><span class="font-size-default"><span>{{ $t('room') }}</span></span></th>
                            <th class="datatable-th pt-2"><span class="font-size-default"><span>{{ $t('staying') }}</span></span></th>
                            <th class="datatable-th pt-2"><span class="font-size-default"><span>{{ $t('cleaning') }}</span></span></th>
                            <th class="datatable-th pt-2"><span class="font-size-default"><span>{{ $t('options') }}</span></span></th>
                            <th class="datatable-th pt-2"><span class="font-size-default"><span>{{ $t('start_time') }}</span></span></th>
                            <th class="datatable-th pt-2"><span class="font-size-default"><span>{{ $t('end_time') }}</span></span></th>
                            <th class="datatable-th pt-2"><span class="font-size-default"><span>{{ $t('payment') }}</span></span></th>
                            <th class="datatable-th pt-2"><span class="font-size-default"><span>{{ $t('confirmed') }}</span></span></th>
                            <th class="datatable-th pt-2"><span class="font-size-default"><span>{{ $t('created_by') }}</span></span></th>
                            <th class="datatable-th pt-2"><span class="font-size-default"><span>{{ $t('edit_by') }}</span></span></th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="row in rows" :key="row.id">
                            <td class="datatable-td text-nowrap">{{ formatDateToLocal(row.date) }}</td>
                            <td class="datatable-td text-nowrap">{{ row.hotel ? row.hotel.name : '' }}</td>
                            <td class="datatable-td">{{ getStaffDisplay(row.maid) }}</td>
                            <td class="datatable-td text-nowrap">{{ getRoomDisplay(row) }}</td>
                            <td class="datatable-td text-nowrap">{{ row.staying ? row.staying.name : '' }}</td>
                            <td class="datatable-td text-nowrap">{{ row.cleaning ? row.cleaning.name : '' }}</td>
                            <td class="datatable-td">
                                <div v-if="row.options && row.options.length">
                                    <div v-for="(option, optionIndex) in row.options" :key="`${row.id}-option-${optionIndex}`">
                                        {{ option.name || '' }} ({{ option.quantity || 0 }})
                                    </div>
                                </div>
                            </td>
                            <td class="datatable-td text-nowrap">{{ row.start_time || '' }}</td>
                            <td class="datatable-td text-nowrap">{{ row.end_time || '' }}</td>
                            <td class="datatable-td text-nowrap">{{ formatPayment(row.payment) }}</td>
                            <td class="datatable-td text-nowrap">{{ getConfirmedDisplay(row.confirmed) }}</td>
                            <td class="datatable-td text-nowrap">{{ getUserDisplay(row.created) }}</td>
                            <td class="datatable-td text-nowrap">{{ getUserDisplay(row.edited) }}</td>
                        </tr>
                        </tbody>
                    </table>
                </div>
                <div v-else style="min-height: 400px;" class="position-relative"
                     :class="{'loading-opacity': loading}">
                    <app-empty-data-block :message="$t('empty_data_block_dummy_message')"/>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {TENANT_BASE_URL} from "../../../../../common/Config/UrlHelper";
import {axiosGet} from "../../../../../common/Helper/AxiosHelper";
import {formatDateForServer, formatDateToLocal} from "../../../../../common/Helper/Support/DateTimeHelper";

export default {
    name: "RoomHousekeepingDetailTab",
    props: {
        hotelId: {
            type: [Number, String],
            default: null,
        },
        maidId: {
            type: [Number, String],
            default: 0,
        },
        employeeTags: {
            type: [Array, String],
            default: '',
        },
        fromDate: {
            type: String,
            default: '',
        },
        toDate: {
            type: String,
            default: '',
        },
        passcode: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            loading: false,
            rows: [],
            hasMounted: false,
        };
    },
    computed: {
        requestSignature() {
            return [
                this.hotelId || '',
                this.maidId || 0,
                this.normalizedEmployeeTags,
                this.fromDate || '',
                this.toDate || '',
                this.passcode || '',
            ].join('|');
        },
        normalizedEmployeeTags() {
            if (Array.isArray(this.employeeTags)) {
                return this.employeeTags.join(',');
            }
            return this.employeeTags || '';
        }
    },
    watch: {
        requestSignature() {
            if (!this.hasMounted) {
                return;
            }

            this.loadRows();
        }
    },
    mounted() {
        this.hasMounted = true;
        this.loadRows();
    },
    methods: {
        formatDateToLocal,
        buildUrl() {
            const params = new URLSearchParams({
                hotel_id: this.hotelId || '',
                date: [
                    this.fromDate ? formatDateForServer(this.fromDate) : '',
                    this.toDate ? formatDateForServer(this.toDate) : '',
                ].filter(Boolean).join(','),
                passcode: this.passcode || '',
                maid_id: this.maidId || 0,
                employee_tags: this.normalizedEmployeeTags,
            });

            return `${TENANT_BASE_URL}api/hotel/house-keeping/share?${params.toString()}`;
        },
        clearRows() {
            this.rows = [];
            this.loading = false;
        },
        loadRows() {
            if (!this.hotelId) {
                this.clearRows();
                return;
            }

            this.loading = true;

            axiosGet(this.buildUrl())
                .then(({data}) => {
                    this.rows = Array.isArray(data) ? data : [];
                })
                .catch(error => {
                    this.rows = [];
                    this.$toastr.e(error.response?.data?.message || error.message);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        getStaffDisplay(users) {
            if (!users || !users.length) {
                return '';
            }

            return users.map(user => `${user.full_name || ''}${user.employee_id ? ` (${user.employee_id})` : ''}`).join(', ');
        },
        getRoomDisplay(row) {
            const roomNumber = row.room?.number || '';
            const roomType = row.room_type?.name || row.room?.type?.name || '';

            return [roomNumber, roomType].filter(Boolean).join('-');
        },
        formatPayment(value) {
            return Number(value || 0).toFixed(2);
        },
        getConfirmedDisplay(value) {
            return Number(value) ? this.$t('yes') : this.$t('no');
        },
        getUserDisplay(user) {
            if (!user) {
                return '';
            }

            return user.full_name || [user.first_name, user.last_name].filter(Boolean).join(' ');
        },
    }
}
</script>
